import React from 'react';
import { css } from '@emotion/react';
import { colors } from '../../primitives/colors';
import Link from '../../primitives/link';
import Image from '../../primitives/image';
import Typography from '../../primitives/typography';
import { getColumnWidthAndOffset } from './utils';
import { MenuCategory } from '../types';

const styles = {
    linkRoot: css({
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingBottom: '5px',
        borderBottom: `1px solid ${colors.grey200}`,
        transition: '0.2s',
        '&:hover': {
            borderBottom: `1px solid ${colors.primaryOrange}`,
        },
    }),
    linkIcon: css({
        width: '32px',
        height: '32px',
    }),
    linkTitle: css({
        transition: '0.2s',
        '[role="group"]:hover &': {
            color: colors.primaryOrange,
        },
    }),
    subgroupContainer: css({
        display: 'flex',
        flexFlow: 'wrap',
        width: '100%',
        columnGap: '36px',
    }),
    nestedSubgroupList: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '7px',
        marginTop: '7px',
    }),
    nestedSubgroupLink: css({
        display: 'inline-block',
        width: '100%',
        transition: '0.2s',
        '&:hover': {
            color: colors.primaryOrange,
        },
    }),
    subgroupList: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '7px',
    }),
    subgroupListItem: css({
        width: '100%',
    }),
    subgroupLink: css({
        display: 'inline-block',
        width: '100%',
        transition: '0.2s',
        '&:hover': {
            color: colors.primaryOrange,
        },
    }),
};

const FirstLevelChild = ({ item }: { item: MenuCategory }): JSX.Element => {
    const { categoryLink, icon, subgroup } = item;
    const { href, title } = categoryLink;

    return (
        <div>
            <Link
                to={href}
                role="group"
                css={styles.linkRoot}
            >
                {icon && (
                    <Image
                        css={styles.linkIcon}
                        image={icon}
                    />
                )}
                <Typography
                    fontSize={{
                        dt: 'xl',
                    }}
                    color="shadesBlack"
                    css={styles.linkTitle}
                >
                    {title}
                </Typography>
            </Link>
            {(subgroup && subgroup.length > 0) && (
                <div
                    css={styles.subgroupContainer}
                    style={{
                        marginLeft: icon ? '38px' : '0', // 32px + 6px gap
                        marginTop: '7px',
                    }}
                >
                    {subgroup.map((subgroupItem, subgroupIndex) => {
                        const { subgroupLink, links } = subgroupItem;

                        const { title } = subgroupLink;

                        const {
                            widthPercent,
                            offsetPixels,
                        } = getColumnWidthAndOffset(1, 36, subgroup.length);

                        if (title) {
                            // This should be treated as a nested subgroup

                            return (
                                <div
                                    key={`subgroup-${title}-${subgroupLink.href}`}
                                    style={{
                                        width: `calc(${widthPercent}% - ${offsetPixels}px)`,
                                    }}
                                >
                                    <Typography
                                        fontSize={{
                                            dt: 'lg',
                                        }}
                                        fontWeight="medium"
                                    >
                                        {title}
                                    </Typography>
                                    <ul css={styles.nestedSubgroupList}>
                                        {links.map((link) => {
                                            if (!link) return null;

                                            const { href, title } = link;

                                            return (
                                                <li key={`nested-link-${href}-${title}`}>
                                                    <Typography
                                                        as={Link}
                                                        to={href}
                                                        fontSize={{
                                                            dt: 'lg',
                                                        }}
                                                        css={styles.nestedSubgroupLink}
                                                    >
                                                        {title}
                                                    </Typography>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        }

                        return (
                            <ul
                                key={`subgroup-links-${subgroupLink.href || subgroupIndex}`}
                                css={styles.subgroupList}
                                style={{
                                    width: `calc(${widthPercent}% - ${offsetPixels}px)`,
                                }}
                            >
                                {links.map((link) => {
                                    if (!link) return null;

                                    const { href, title } = link;

                                    return (
                                        <li key={`link-${href}-${title}`} css={styles.subgroupListItem}>
                                            <Typography
                                                as={Link}
                                                to={href}
                                                fontSize={{
                                                    dt: 'lg',
                                                }}
                                                css={styles.subgroupLink}
                                            >
                                                {title}
                                            </Typography>
                                        </li>
                                    );
                                })}
                            </ul>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FirstLevelChild;
